import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';

import Button from '@/componentsv2/Button';
import DatePicker from '@/componentsv2/DatePicker';
import Dialog from '@/componentsv2/Dialog';
import Input from '@/componentsv2/Input';

export interface PutOnHoldModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (input: { holdReason: string; holdResumeDate: string }) => void;
  submitting?: boolean;
  error?: string | null;
}

/**
 * Put-on-hold confirm modal (ENG-5290 T2). Reason is mandatory; the expected
 * resume date is mandatory and must be in the future (api rejects otherwise).
 */
export function PutOnHoldModal({
  open,
  onCancel,
  onConfirm,
  submitting,
  error,
}: PutOnHoldModalProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [resumeDate, setResumeDate] = useState<Moment | null>(null);

  // The dialog stays mounted when closed, so reset the form each time it opens.
  useEffect(() => {
    if (open) {
      setReason('');
      setResumeDate(null);
    }
  }, [open]);

  const resumeInFuture = !!resumeDate && resumeDate.isAfter(moment(), 'day');
  const canConfirm = reason.trim().length > 0 && resumeInFuture && !submitting;

  function handleConfirm() {
    if (!resumeDate) return;
    onConfirm({ holdReason: reason.trim(), holdResumeDate: resumeDate.toISOString() });
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" disablePortal={false}>
      <div data-testid="er-put-on-hold-modal" className="flex flex-col gap-16 p-24">
        <h2 className="hd-5">Put request on hold</h2>
        <p className="text-on-surface-secondary body-1">
          Expert sourcing is paused, but previously invited experts can still apply.
        </p>
        <p className="text-on-surface-secondary body-1">
          Add an internal note stating the reason for putting the request on hold and when you
          expect to resume. This is only shared with the OnFrontiers team and project members.
        </p>

        <Input
          label="Reason"
          required
          multiline
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this request being paused?"
        />

        <div className="flex flex-col gap-4">
          <span className="text-on-surface-secondary subtitle-2">
            Expected resume date (required)
          </span>
          <DatePicker
            includeFutureDatePick
            minDate={moment().add(1, 'day')}
            value={resumeDate}
            onChange={(v) => setResumeDate(moment.isMoment(v) ? v : null)}
            placeholder="mm/dd/yyyy"
          />
        </div>

        {error ? <p className="text-red-500 body-2">{error}</p> : null}

        <div className="flex justify-end gap-8">
          <Button variant="tertiary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            Put on hold
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
